import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CountUp from 'react-countup';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, UserGroupIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';

const LiveProtectionCounter = React.memo(() => {
  const [stats, setStats] = useState({
    protectedClients: 2847,
    activeAudits: 23,
    monthlySubscribers: 156
  });

  // Memoize format function
  const formatNumber = useCallback((value) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  }, []);

  // Reduce update frequency and optimize updates
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        protectedClients: prev.protectedClients + (Math.random() > 0.9 ? 1 : 0), // Very occasional updates
        activeAudits: prev.activeAudits + (Math.random() > 0.95 ? 1 : 0), // Rare updates
        monthlySubscribers: prev.monthlySubscribers + (Math.random() > 0.85 ? 1 : 0) // Occasional updates
      }));
    }, 30000); // Much less frequent updates

    return () => clearInterval(interval);
  }, []);

  // Memoize animation variants
  const cardVariants = useMemo(() => ({
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    whileHover: { scale: 1.02 }, // Reduced from 1.05
    transition: { duration: 0.5 }
  }), []);

  const pulseVariants = useMemo(() => ({
    animate: {
      scale: [1, 1.1, 1], // Reduced scale
      opacity: [1, 0.8, 1]
    },
    transition: {
      duration: 3, // Increased duration
      repeat: Infinity,
      ease: "easeInOut"
    }
  }), []);

  // Memoize stat cards data
  const statCards = useMemo(() => [
    {
      title: "Protected Clients",
      value: stats.protectedClients,
      icon: UserGroupIcon,
      subtitle: "Businesses under protection",
      color: "blue"
    },
    {
      title: "Active Audits",
      value: stats.activeAudits,
      icon: ShieldCheckIcon,
      subtitle: "Currently defending",
      color: "purple"
    },
    {
      title: "Monthly Subscribers",
      value: stats.monthlySubscribers,
      icon: CurrencyDollarIcon,
      subtitle: "Monthly protection plans",
      color: "green"
    }
  ], [stats]);

  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      animate="animate"
      transition={cardVariants.transition}
      className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-2xl p-8 text-white"
    >
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold mb-2">Live Protection Stats</h3>
        <p className="text-blue-200">Watch our impact grow in real-time</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            variants={cardVariants}
            whileHover={cardVariants.whileHover}
            transition={{ duration: 0.2 }} // Reduced hover transition
            className="bg-white/10 rounded-xl p-6 backdrop-blur-lg"
          >
            <div className="flex items-center mb-4">
              <stat.icon className={`h-8 w-8 text-${stat.color}-300`} />
              <h4 className="ml-3 text-lg font-semibold">{stat.title}</h4>
            </div>
            <div className={`text-3xl font-bold text-${stat.color}-300`}>
              <CountUp
                end={stat.value}
                formattingFn={formatNumber}
                duration={1.5} // Reduced duration
                preserveValue={true} // Prevent re-animation on every render
              />
            </div>
            <div className="mt-2 text-sm text-blue-200">
              {stat.subtitle}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Optimized Live Indicator */}
      <div className="mt-8 flex items-center justify-center">
        <motion.div
          variants={pulseVariants}
          animate="animate"
          transition={pulseVariants.transition}
          className="w-2 h-2 bg-red-500 rounded-full mr-2"
        />
        <span className="text-sm text-blue-200">Live Updates</span>
      </div>
    </motion.div>
  );
});

LiveProtectionCounter.displayName = 'LiveProtectionCounter';

export default LiveProtectionCounter; 